import { isSelectableStudyAudienceStatus } from '@/lib/audience-selectability'

type AudienceStageMetadata = {
  titleFont?: string | null
  selectedCoverPhotoIndex?: number | null
  coverPhotoOptions?: Array<{ url: string; thumbnailUrl?: string }> | null
} | null | undefined

type AudiencePresentationSource = {
  coverImageUrl?: string | null
  stageMetadata?: AudienceStageMetadata
} | null | undefined

function normalizeSelectedCoverIndex(
  selectedCoverPhotoIndex: number | null | undefined,
  optionCount: number,
) {
  if (optionCount === 0) return null
  if (
    typeof selectedCoverPhotoIndex !== 'number' ||
    !Number.isInteger(selectedCoverPhotoIndex)
  ) {
    return 0
  }
  return Math.min(Math.max(selectedCoverPhotoIndex, 0), optionCount - 1)
}

export function resolveAudiencePersistedCoverUrl(
  source: AudiencePresentationSource,
) {
  const coverPhotoOptions =
    source?.stageMetadata?.coverPhotoOptions?.filter(
      (option): option is { url: string; thumbnailUrl?: string } =>
        typeof option?.url === 'string' && option.url.trim().length > 0,
    ) ?? []
  const selectedCoverIndex = normalizeSelectedCoverIndex(
    source?.stageMetadata?.selectedCoverPhotoIndex,
    coverPhotoOptions.length,
  )
  return selectedCoverIndex == null
    ? null
    : coverPhotoOptions[selectedCoverIndex]?.url ?? null
}

export function resolveAudienceCanonicalCoverUrl(
  source: AudiencePresentationSource,
) {
  return (
    resolveAudiencePersistedCoverUrl(source) ??
    source?.coverImageUrl ??
    null
  )
}

type SetupPickerAudienceSource = AudiencePresentationSource & {
  status?: string | null
}

/**
 * Audiences a study can be fielded on, with a cover. `ready` and
 * `relaunching` match the study builder; relaunching still has to confirm
 * before it is selected. Failed, processing, and missing-cover rows stay out.
 */
export function isSetupPickerAudience(
  source: SetupPickerAudienceSource | null | undefined,
): boolean {
  if (!source || !isSelectableStudyAudienceStatus(source.status)) {
    return false
  }
  return Boolean(resolveAudienceCanonicalCoverUrl(source))
}

export function resolveAudienceTitleFont(
  source: AudiencePresentationSource,
) {
  const titleFont = source?.stageMetadata?.titleFont
  return typeof titleFont === 'string' && titleFont.trim().length > 0
    ? titleFont.trim()
    : 'Oswald'
}

type AudienceTwinCountSource = {
  status?: string | null
  rowCount?: number | null
  personaCount?: number | null
}

/**
 * Count to show as "digital twins": declared runtime row count when available,
 * with persona count only as a fallback for legacy/missing row-count payloads.
 */
export function resolveAudienceDigitalTwinCount(
  source: AudienceTwinCountSource | null | undefined,
): number | null {
  if (!source) return null
  const rc = source.rowCount
  const pc = source.personaCount
  if (typeof rc === 'number') return rc
  if (typeof pc === 'number') return pc
  return null
}

export function buildAudiencePresentationSignature(
  source: AudiencePresentationSource & {
    id?: string
    status?: string
    sharingMode?: string
    aiDescription?: string | null
    aiTags?: string[]
    rowCount?: number | null
    personaCount?: number | null
  },
) {
  const resolvedCoverUrl = resolveAudienceCanonicalCoverUrl(source) ?? ''
  const titleFont = resolveAudienceTitleFont(source) ?? ''
  const selectedCoverIndex =
    typeof source?.stageMetadata?.selectedCoverPhotoIndex === 'number'
      ? source.stageMetadata.selectedCoverPhotoIndex
      : ''

  return [
    source?.id ?? '',
    source?.status ?? '',
    source?.sharingMode ?? 'shared',
    resolvedCoverUrl,
    titleFont,
    selectedCoverIndex,
    source?.aiDescription ? '1' : '0',
    (source?.aiTags ?? []).length,
    source?.rowCount ?? '',
    source?.personaCount ?? '',
  ].join('|')
}
